import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
} from '@mui/material';

export interface InsertLayoutDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (templateColumns: string) => void;
}

const LAYOUT_OPTIONS = [
  {
    name: '2 columns (equal width)',
    templateColumns: '1fr 1fr',
    preview: ['50%', '50%'],
  },
  {
    name: '2 columns (25% - 75%)',
    templateColumns: '1fr 3fr',
    preview: ['25%', '75%'],
  },
  {
    name: '2 columns (75% - 25%)',
    templateColumns: '3fr 1fr',
    preview: ['75%', '25%'],
  },
  {
    name: '3 columns (equal width)',
    templateColumns: '1fr 1fr 1fr',
    preview: ['33%', '33%', '33%'],
  },
  {
    name: '3 columns (25% - 50% - 25%)',
    templateColumns: '1fr 2fr 1fr',
    preview: ['25%', '50%', '25%'],
  },
  {
    name: '4 columns (equal width)',
    templateColumns: '1fr 1fr 1fr 1fr',
    preview: ['25%', '25%', '25%', '25%'],
  },
];

const LayoutPreview: React.FC<{ columns: string[] }> = ({ columns }) => (
  <Box
    sx={{
      display: 'flex',
      gap: '2px',
      height: '40px',
      width: '100%',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden',
    }}
  >
    {columns.map((width, index) => (
      <Box
        key={index}
        sx={{
          flex: width.replace('%', ''),
          backgroundColor: 'primary.light',
          opacity: 0.7,
          '&:nth-of-type(odd)': {
            opacity: 0.5,
          },
        }}
      />
    ))}
  </Box>
);

export const InsertLayoutDialog: React.FC<InsertLayoutDialogProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  const handleSelect = (templateColumns: string) => {
    onSelect(templateColumns);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Insert Columns Layout</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {LAYOUT_OPTIONS.map((option, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 2,
                    transform: 'translateY(-2px)',
                  },
                }}
                onClick={() => handleSelect(option.templateColumns)}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {option.name}
                  </Typography>
                  <LayoutPreview columns={option.preview} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
